import moment from 'moment';

import type { AdModel, AdsFilteringType } from '@/types';
import {
  ActivityKindEnum,
  AdFieldLandKindEnum,
  AdFieldMultilistingEnum,
  AdFieldOccupancyEnum,
  FileKindEnum,
  PropertyKindEnum,
  RentPeriodEnum,
  TransactionTypeEnum,
} from '@/types';

type PreferredPropertyKinds =
  | 'shop'
  | 'office'
  | 'warehouse'
  | 'residentialLand'
  | 'industrialLand'
  | 'commercialLand';

export const rentFrequencyLabels: Record<RentPeriodEnum, string> = {
  [RentPeriodEnum.Yearly]: 'Yearly',
  [RentPeriodEnum.Monthly]: 'Monthly',
  [RentPeriodEnum.Weekly]: 'Weekly',
  [RentPeriodEnum.Daily]: 'Daily',
};

const transactionTypeMapping = {
  [TransactionTypeEnum.Ready]: 'Ready',
  [TransactionTypeEnum.Resale]: 'Resale',
  [TransactionTypeEnum.Distress]: 'Distress',
};

const availabilityTypeMapping = {
  [AdFieldOccupancyEnum.Vacant]: 'Vacant',
  [AdFieldOccupancyEnum.Tenanted]: 'Rented',
};

const multilistingLabel = {
  [AdFieldMultilistingEnum.Covered]: 'Covered',
  [AdFieldMultilistingEnum.NotCovered]: 'Not Covered',
};

const preferredPropertyKinds = [
  PropertyKindEnum.Shop,
  PropertyKindEnum.Office,
  PropertyKindEnum.Warehouse,
  PropertyKindEnum.ResidentialLand,
  PropertyKindEnum.IndustrialLand,
  PropertyKindEnum.CommercialLand,
];

const propertyKindLabel: Record<PreferredPropertyKinds, string> = {
  shop: 'Shop',
  office: 'Office',
  warehouse: 'Warehouse',
  residentialLand: 'Residential Land',
  industrialLand: 'Industrial Land',
  commercialLand: 'Commercial Land',
};

const getPrice = (item: AdModel, filterRentFrequency?: RentPeriodEnum) => {
  if (item.activityKind === ActivityKindEnum.Sale) {
    return item.transactionType === TransactionTypeEnum.Distress
      ? item.convertedSellingPrice || 0
      : item.convertedPrice || 0;
  }

  const rentFrequency = filterRentFrequency || item.defaultRentFrequency;

  switch (rentFrequency) {
    case RentPeriodEnum.Yearly:
      return item.convertedPriceYearly || 0;
    case RentPeriodEnum.Monthly:
      return item.convertedPriceMonthly || 0;
    case RentPeriodEnum.Weekly:
      return item.convertedPriceWeekly || 0;
    case RentPeriodEnum.Daily:
      return item.convertedPriceDaily || 0;
    default:
      return 0;
  }
};

const getArea = (item: AdModel) => {
  const convertedPlotArea = item.plotArea
    ? item.convertedAreaUnits === 'SQFT'
      ? item.plotArea
      : Math.round(Number(item.plotArea) / 10.764)
    : 0;

  return item.convertedArea ? item.convertedArea : item.plotArea ? convertedPlotArea : 0;
};

const getTransactionDate = (item: AdModel) => {
  const transactionTypeText = item.transactionType && transactionTypeMapping[item.transactionType];
  const isOccupancy =
    item.occupancy === AdFieldOccupancyEnum.Vacant ||
    item.occupancy === AdFieldOccupancyEnum.Tenanted;
  const availabilityTypeText =
    item.occupancy && isOccupancy ? ` | ${availabilityTypeMapping[item.occupancy]}` : '';
  const rentedTillText = item.rentedTill ? ` | ${moment(item.rentedTill).format('MMM YYYY')}` : '';
  const distressPercentFee =
    item.price && item.sellingPrice
      ? ` | ${(
          ((Number(item.price) - Number(item.sellingPrice)) / Number(item.price)) *
          100
        ).toFixed(1)}%`
      : '';

  return `${transactionTypeText}${availabilityTypeText}${rentedTillText}${distressPercentFee}`;
};

const getTitleText = (item: AdModel) => {
  const isPreferredTitle = preferredPropertyKinds.includes(item.propertyKind);
  const titlePrefix =
    item.propertyKind && propertyKindLabel[item.propertyKind as PreferredPropertyKinds];

  return isPreferredTitle ? `${titlePrefix} at ${item.nameEn}` : item.nameEn;
};

const getParsedUpdateDate = (updatedAt: string) => {
  const isUpdatedToday = moment().isSame(updatedAt, 'day');

  return moment(updatedAt).format(`${isUpdatedToday ? '[today]' : 'DD.MM.YYYY'} hh:mm a`);
};

export const createAdsListItemViewModel = (item: AdModel, filteringObj?: AdsFilteringType) => {
  const images = item.propertyAdMedia
    .filter((media) => media.kind === FileKindEnum.Image)
    .sort((firstMedia, secondMedia) => firstMedia.order - secondMedia.order);
  const filterRentFrequency = filteringObj?.rentFrequency;

  return {
    address: item.addressEn || item.nameEn,
    area: getArea(item),
    filterRentFrequency,
    hasVideo: item.propertyAdMedia.some((media) => media.kind === FileKindEnum.Video),
    images,
    isNotCommercial: item.landKind !== AdFieldLandKindEnum.Commercial,
    isSale: item.activityKind === ActivityKindEnum.Sale,
    multilistingText: item.multilisting && multilistingLabel[item.multilisting],
    parsedUpdateDate: getParsedUpdateDate(item.updatedAt),
    price: getPrice(item, filterRentFrequency),
    textBaths: Number(item.baths) > 1 ? 'baths' : 'bath',
    textBeds: item.beds === 'Studio' ? '' : Number(item.beds) > 1 ? 'beds' : 'bed',
    titleText: getTitleText(item),
    transactionDate: getTransactionDate(item),
  };
};
